import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import type { RootState } from "_shared/_stores/redux-store";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import Guid from "_shared/guids/guid";
import { messageListTag, messagingApi, sharedFilesTag, unreadForumsTag } from "messaging/_api/messaging-api";
import { MessageJSON } from "messaging/_schemas/message-schema";
import { addMessage, removeMessage } from "messaging/_stores/messages-redux-slice";
import {
  isLoadingSharedFilesOf,
  sharedFileProcessingReported,
} from "messaging/attachments/_stores/message-attachments-redux-slice";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { forumUnreadCountReported } from "messaging/forums/_stores/forum-unread-counts-redux-slice";
import { withUpdatedAttachment, type AttachmentProcessingUpdate } from "messaging/_stores/_realtime/attachment-processing-update";
import Message from "messaging/message";

const toMessage = (payload: unknown) =>
  Message.fromJSON({
    json: payload as MessageJSON,
  });

/**
 * Every message event carries the recipient's own unread count for its forum, counted after the change
 * it announces (#5828). It is what moves a tab the reader is not looking at.
 */
function reportUnreadCount(message: Message) {
  const forumId = message.forum?.id?.value;
  const unreadCount = message.forum?.numUnreadMessages;
  return forumId && typeof unreadCount === "number" ? forumUnreadCountReported({ forumId, unreadCount }) : undefined;
}

/** The update as the Shared files lists take it, or undefined for a payload that does not parse. */
function toSharedFileProcessingReport(update: AttachmentProcessingUpdate) {
  try {
    return {
      forumId: new Guid(update.forumId),
      messageId: new Guid(update.messageId),
      file: MessageFileInfo.fromJSON(update.attachment),
    };
  } catch {
    // A malformed event changes nothing: the list reads the processing fresh when it next loads.
    return undefined;
  }
}

const callbacks = withCallbacks()
  .add("message-received", (payload: unknown) => (dispatch) => {
    const message = toMessage(payload);
    dispatch(addMessage(message));
    const unreadCount = reportUnreadCount(message);
    if (unreadCount) {
      dispatch(unreadCount);
      // A conversation joins the unread ones when a message lands in it (#6583).
      dispatch(messagingApi.util.invalidateTags([unreadForumsTag]));
    }
  })
  // Edits and soft deletes both replace the row in place. A delete payload is already a body-free
  // Message tombstone, so upserting it gives realtime readers the same projection as a cold refresh.
  .add("message-updated", (payload: unknown) => (dispatch) => {
    const message = toMessage(payload);
    dispatch(addMessage(message));
    const unreadCount = reportUnreadCount(message);
    if (unreadCount) {
      dispatch(unreadCount);
      // A retraction or a delete can take a conversation's last unread message away.
      dispatch(messagingApi.util.invalidateTags([unreadForumsTag]));
    }
  })
  // A file of a message the reader has open finished a step of its processing (#6877).
  .add("attachment-processing-updated", (payload: unknown) => (dispatch, getState) => {
    const state = getState() as RootState;
    const update = payload as AttachmentProcessingUpdate;
    const updated = withUpdatedAttachment(state, update);
    if (updated) dispatch(addMessage(updated));
    // The Shared files lists hold the file too, including for a forum whose messages are not loaded (#6986).
    const report = toSharedFileProcessingReport(update);
    if (report) dispatch(sharedFileProcessingReported(report));
    // A page of this forum still loading may have read the file before this step finished. It would
    // store that older copy over this one, and no later update would come to correct it. So the
    // pages are read again once the outstanding request completes.
    if (state.messages?.loading[update.forumId]) dispatch(messagingApi.util.invalidateTags([messageListTag]));
    if (isLoadingSharedFilesOf(state, update.forumId)) dispatch(messagingApi.util.invalidateTags([sharedFilesTag]));
  })
  .add("message-deleted", (payload: unknown) => (dispatch) => {
    const id = new Guid((payload as { id: string }).id);
    dispatch(removeMessage(id));
  });

export const messagesAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.messages,
  connectionHealth: { key: "messaging", label: "Messaging" },
  shouldConnectionStartImmediately: true,
});
